/**
 * Where the route arrows and the screen pointer lead (docs/UI.md "Markers and pointers"). Pure; tested in
 * routeGoal.test.ts. The rule, in the owner's words: arrows only for an ACTIVE job or a place I SELECTED.
 *
 *  1. A job is running (an `objective` marker is up — offline story / street job / export / race / cab /
 *     mayhem, online courier job or freight run): its next stop.
 *  2. …unless you chose a place AFTER the job began (a tap on the map → Route here, the book's "Mark on the
 *     map"): you asked for it, so that, until you get there or rub it out; then the job's again. A mark set
 *     before the job waits until the job is over.
 *  3. No job: your mark.
 *  4. Otherwise nothing — never "the nearest marker", never a suggestion, a shop, an event or the turf.
 */
import type { MapMarker } from '../ui/uiTypes';

export interface RouteMark {
  x: number;
  z: number;
  label: string;
  /** Clock (s) when it was chosen. */
  at: number;
}

export interface RouteGoal {
  x: number;
  z: number;
  /** 'job' = the running job's next stop; 'mark' = the place you chose. */
  kind: 'job' | 'mark';
  label: string;
}

/** The job's next stop counts as "the same" one while it stays this close (m): moving targets are followed. */
const SAME_STOP = 2.5;

/**
 * The goal now. `jobSince` is the clock when the job's objective first appeared (null: no job); `prev` the
 * goal last frame, so with two objectives up (rare) the arrows don't flick between them.
 */
export function pickRouteGoal(
  markers: readonly MapMarker[],
  mark: RouteMark | null,
  jobSince: number | null,
  player: { x: number; z: number },
  prev: RouteGoal | null,
): RouteGoal | null {
  let best: MapMarker | null = null;
  let bestD = Infinity;
  let keep: MapMarker | null = null;
  for (const m of markers) {
    if (m.kind !== 'objective' || m.area) continue;
    if (prev?.kind === 'job' && Math.abs(m.x - prev.x) < SAME_STOP && Math.abs(m.z - prev.z) < SAME_STOP) keep = m;
    const d = (m.x - player.x) ** 2 + (m.z - player.z) ** 2;
    if (d < bestD) {
      bestD = d;
      best = m;
    }
  }
  const job = keep ?? best;
  if (mark && (!job || (jobSince !== null && mark.at > jobSince))) return { x: mark.x, z: mark.z, kind: 'mark', label: mark.label };
  if (job) return { x: job.x, z: job.z, kind: 'job', label: job.label ?? '' };
  return null;
}

/** Is any job objective on the map this frame? */
export function jobUp(markers: readonly MapMarker[]): boolean {
  for (const m of markers) if (m.kind === 'objective' && !m.area) return true;
  return false;
}
